import { useState } from 'react';
import { Card } from '../ui';

interface OrderBookEntry {
  price: number;
  quantity: number;
  total: number;
}

interface OrderBookDisplayProps {
  bids: OrderBookEntry[];
  asks: OrderBookEntry[];
  lastPrice?: number;
  spread?: number;
  depth?: number;
}

export function OrderBookDisplay({ 
  bids, 
  asks, 
  lastPrice, 
  spread,
  depth = 10 
}: OrderBookDisplayProps) {
  const [view, setView] = useState<'both' | 'bids' | 'asks'>('both');

  const formatPrice = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100);
  };

  const formatQuantity = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 4 }).format(value);
  };

  const maxBidTotal = Math.max(...bids.slice(0, depth).map(b => b.total), 1);
  const maxAskTotal = Math.max(...asks.slice(0, depth).map(a => a.total), 1);

  const displayBids = bids.slice(0, depth);
  const displayAsks = asks.slice(0, depth).reverse();

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-surface-100">Livro de Ofertas</h3>
        <div className="flex gap-1 bg-surface-800 rounded-lg p-1">
          <button
            onClick={() => setView('both')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              view === 'both' ? 'bg-surface-700 text-surface-100' : 'text-surface-400 hover:text-surface-200'
            }`}
          >
            Ambos
          </button>
          <button
            onClick={() => setView('bids')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              view === 'bids' ? 'bg-green-500/20 text-green-400' : 'text-surface-400 hover:text-surface-200'
            }`}
          >
            Compra
          </button>
          <button
            onClick={() => setView('asks')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              view === 'asks' ? 'bg-red-500/20 text-red-400' : 'text-surface-400 hover:text-surface-200'
            }`}
          >
            Venda
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="grid grid-cols-3 text-xs text-surface-500 mb-2 px-2">
        <span>Preço</span>
        <span className="text-center">Quantidade</span>
        <span className="text-right">Total</span>
      </div>

      {/* Asks (Sell orders) */}
      {(view === 'both' || view === 'asks') && (
        <div className="space-y-1 mb-2">
          {displayAsks.map((ask, i) => (
            <div key={`ask-${i}`} className="relative">
              <div 
                className="absolute inset-y-0 right-0 bg-red-500/10"
                style={{ width: `${(ask.total / maxAskTotal) * 100}%` }}
              />
              <div className="relative grid grid-cols-3 text-sm py-1 px-2">
                <span className="text-red-400">{formatPrice(ask.price)}</span>
                <span className="text-center text-surface-300">{formatQuantity(ask.quantity)}</span>
                <span className="text-right text-surface-400">{formatQuantity(ask.total)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Spread / Last Price */}
      {view === 'both' && (
        <div className="py-2 px-2 bg-surface-800/50 rounded my-2 flex items-center justify-between">
          <span className="text-surface-400 text-xs">Último Preço</span>
          <span className="font-semibold text-surface-100">{lastPrice ? formatPrice(lastPrice) : '-'}</span>
          {spread !== undefined && (
            <span className="text-xs text-surface-500">Spread: {spread.toFixed(2)}%</span>
          )}
        </div>
      )}

      {/* Bids (Buy orders) */}
      {(view === 'both' || view === 'bids') && (
        <div className="space-y-1">
          {displayBids.map((bid, i) => (
            <div key={`bid-${i}`} className="relative">
              <div 
                className="absolute inset-y-0 left-0 bg-green-500/10"
                style={{ width: `${(bid.total / maxBidTotal) * 100}%` }}
              />
              <div className="relative grid grid-cols-3 text-sm py-1 px-2">
                <span className="text-green-400">{formatPrice(bid.price)}</span>
                <span className="text-center text-surface-300">{formatQuantity(bid.quantity)}</span>
                <span className="text-right text-surface-400">{formatQuantity(bid.total)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {displayBids.length === 0 && displayAsks.length === 0 && (
        <p className="text-center text-surface-500 py-8">Nenhuma ordem no livro</p>
      )}
    </Card>
  );
}
